import PropTypes from 'prop-types';
import { createContext, useCallback, useEffect, useReducer } from 'react';

// utils
import axios from '../utils/axios';
import { isValidToken, setSession } from '../utils/jwt';

// ----------------------------------------------------------------------

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  },
  LOGIN: (state, action) => {
    const { user } = action.payload;
    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
  REGISTER: (state, action) => {
    const { user } = action.payload;
    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  CREATECOMPANY: (state, action) => {
    const { user } = action.payload;
    return {
      ...state,
      user,
    };
  },
  UPDATE_USER_BASIC: (state, action) => {
    const { user } = action.payload;
    return {
      ...state,
      user: {
        ...state.user,
        name: user.name,
        email: user.email,
        picture: user.picture,
        phone: user.phone,
      },
    };
  },
  UPDATECOMPANY: (state, action) => {
    const { company } = action.payload;
    return {
      ...state,
      user: {
        ...state.user,
        company,
      },
    };
  },
};

const reducer = (state, action) => (handlers[action.type] ? handlers[action.type](state, action) : state);

const AuthContext = createContext({
  ...initialState,
  method: 'jwt',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
  createCompany: () => Promise.resolve(),
  updateUserBasic: () => Promise.resolve(),
  updateCompany: () => Promise.resolve(),
});

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const getProfile = useCallback(async () => {
    try {
      // get profile
      const response = await axios.get('/users/profile');
      const user = response.data;
      console.log({ user });

      // create permission dictionary
      const permissions = Object.values(user.role?.permissions || {}).reduce(
        (accum, cur) => ({ ...accum, ...cur }),
        {}
      );
      console.log({ permissions });
      user.permissions = permissions;

      const roleName = user.role?.name || user.role;
      console.log({ roleName });
      user.roleName = roleName;

      return user;
    } catch (error) {
      console.log({ error });
      return null;
    }
  }, []);

  useEffect(() => {
    const initialize = async () => {
      try {
        const accessToken = window.localStorage.getItem('accessToken');
        if (accessToken && isValidToken(accessToken)) {
          setSession({ accessToken });
          const user = await getProfile();
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: true,
              user,
            },
          });
        } else {
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    };

    initialize();
  }, [getProfile]);

  const login = async (phone, password) => {
    const response = await axios.post('/auth/login', {
      phone,
      password,
    });
    const {
      token: { accessToken, refreshToken },
      user,
    } = response.data;
    setSession({ accessToken, refreshToken, user });
    const userProfile = await getProfile();
    dispatch({
      type: 'LOGIN',
      payload: {
        user: userProfile || user,
      },
    });
  };

  const register = async (name, password, phone) => {
    const response = await axios.post('/auth/register', {
      name,
      password,
      phone,
    });
    const {
      token: { accessToken, refreshToken },
      user,
    } = response.data;
    setSession({ accessToken, refreshToken, user });
    window.localStorage.setItem('accessToken', accessToken);
    const userProfile = await getProfile();
    dispatch({
      type: 'REGISTER',
      payload: {
        user: userProfile || user,
      },
    });
  };

  const logout = async () => {
    setSession({ accessToken: null });
    dispatch({ type: 'LOGOUT' });
  };

  const createCompany = async (name, phone, field, website) => {
    const data = { name, phone, field, website };
    const response = await axios.post('/companies', data);
    const { user } = response.data;
    const userProfile = await getProfile();
    dispatch({
      type: 'CREATECOMPANY',
      payload: {
        user: userProfile || user,
      },
    });
  };

  const updateUserBasic = async (user) => {
    if (!user) return;
    dispatch({
      type: 'UPDATE_USER_BASIC',
      payload: {
        user,
      },
    });
  };

  const updateCompany = async (company) => {
    if (!company) return;
    dispatch({
      type: 'UPDATECOMPANY',
      payload: {
        company,
      },
    });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        login,
        logout,
        register,
        createCompany,
        updateUserBasic,
        updateCompany,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
